import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import isInclusivelyAfterDay from '../../../components/DatePicker/momentFunctions';
import DatePicker from '../../../components/DatePicker/DatePicker';
import NumberInput from 'Components/Form/Input/NumberInput/NumberInput';
import { console } from 'window-or-global';
import ReactTooltip from 'react-tooltip';
import ErrorMessage from './ErrorMessage';
import { extendMoment } from 'moment-range';
import Accordion from '../../../components/Accordion';

const moment = extendMoment(Moment);

// Date format settings.
moment.locale(drupalSettings.path.currentLanguage);

const API_DATE_FORMAT = 'YYYY-MM-DD';
const READABLE_DATE_FORMAT = 'D.M.YYYY';

const defaultProps = {
  buttonText: Drupal.t('Book this room'),
  roomAllowsPets: true,
  allowInfants: true,
  selectDatesLabel: Drupal.t('Select dates'),
  popularDatesLabel: Drupal.t('Or choose from the most popular dates'),
  adultsLabel: Drupal.t('Adults 12+ yr'),
  kidsLabel: Drupal.t('Kids 3-11yr'),
  infantsLabel: Drupal.t('Kids 0-2yr'),
  startDatePlaceholderText: Drupal.t('Check in'),
  endDatePlaceholderText: Drupal.t('Check out'),
  hasQuota: true,
  disableBooking: () => true,
  selectedServices: [],
};

const AccommodationSearchForm = (properties) => {
  const props = {
    ...defaultProps,
    ...properties,
  };
  const urlParams = new URLSearchParams(window.location.search);
  const [blockedDate, setBlockedDate] = useState();
  const [restrictedDate, setRestrictedDate] = useState();
  const { startDate, endDate } = props;

  const [datesAvailable, setDatesAvailable] = useState(true);
  const [minimumStayForDate, setMinimumStayFordate] = useState([]);
  const [popularDates, setPopularDates] = useState({
    loading: false,
    data: null,
  });
  const [popularDatesSelected, setPopularDatesSelected] = useState('default');
  const [popularDatesEndValue, setPopularDatesEndValue] = useState();
  const [popularDatesStartValue, setPopularDatesStartValue] = useState();
  const [campaignDates, setCampaignDates] = useState();
  const [availabilityChecked, setAvailabilityChecked] = useState(
    endDate && startDate ? false : true
  );
  const [quickReservationAllowed, setQuickReservationAllowed] = useState(true);

  const hideButtonClass = props.hideButton
    ? 'accommodation-reservation-form--hidden-button'
    : '';

  const minimumAvailabilityMessage = drupalSettings.min_availability_message;
  const unavailableDates = [];
  const limitedDates = [];
  const minimalDates = [];
  const minimumstayDates = [];

  // Check if deadline for same day booking has passed.
  let isBookingDeadline = false;
  const bookingDeadlineValues = drupalSettings.booking_deadline.split(':');
  if (bookingDeadlineValues.length > 1) {
    const bookingDeadline = new Date().setHours(
      bookingDeadlineValues[0],
      bookingDeadlineValues[1]
    );

    if (new Date().getTime() > bookingDeadline) {
      isBookingDeadline = true;
    }
  }

  function selectPopularDates(e) {
    let mStartDate, mEndDate;
    setPopularDatesSelected(e.target.value);

    if (props.setPopularDateValue) {
      props.setPopularDateValue(e.target.value);
    }

    if (e.target.options[e.target.selectedIndex].dataset.start) {
      mStartDate = moment(
        e.target.options[e.target.selectedIndex].dataset.start,
        API_DATE_FORMAT
      );
      props.setStartDate(mStartDate);
      setPopularDatesStartValue(
        e.target.options[e.target.selectedIndex].dataset.start
      );
    } else {
      props.setStartDate(undefined);
    }
    if (e.target.options[e.target.selectedIndex].dataset.end) {
      mEndDate = moment(
        e.target.options[e.target.selectedIndex].dataset.end,
        API_DATE_FORMAT
      );
      props.setEndDate(mEndDate);
      setPopularDatesEndValue(
        e.target.options[e.target.selectedIndex].dataset.end
      );
    } else {
      props.setEndDate(undefined);
    }

    if (mStartDate && mEndDate) {
      const event = popularDates.data.find((tempEvent) => {
        return (
          String(tempEvent.attributes.field_popular_dates_start) ===
            String(mStartDate.format('YYYY-MM-DD')) &&
          String(tempEvent.attributes.field_popular_dates_end) ===
            String(mEndDate.format('YYYY-MM-DD'))
        );
      });

      if (props.setSelectedPopularDate) {
        props.setSelectedPopularDate({
          startDate: mStartDate,
          endDate: mEndDate,
          name: event.attributes.name,
        });
      }
    }
  }

  function overlapsDateRange(a) {
    // if the field has no dates, assume the service is always available for this product
    if (!a.length) {
      return true;
    }
    const parsedDateRanges = [];
    JSON.parse(a).forEach((dateRange) => {
      const range = {
        startDate: moment(dateRange.value, API_DATE_FORMAT),
        endDate: moment(dateRange.end_value, API_DATE_FORMAT),
      };
      parsedDateRanges.push(range);
    });
    return parsedDateRanges.some(
      (range) =>
        !range.startDate.isAfter(endDate) && !range.endDate.isBefore(startDate)
    );
  }

  // Fetch popular dates taxonomy terms from Drupal.
  useEffect(() => {
    setPopularDates({ loading: true });

    const currentDate = moment().utc().format('YYYY-MM-DD');

    // Construct URL params that filter the JSON api results.
    const params = new URLSearchParams({
      // Filter items that start after the current date.
      'filter[future][condition][path]': 'field_popular_dates_start',
      'filter[future][condition][operator]': '>',
      'filter[future][condition][value]': currentDate,

      // Filter published items.
      'filter[published][condition][path]': 'field_popular_dates_publication',
      'filter[published][condition][operator]': '<=',
      'filter[published][condition][value]': currentDate,

      // Filter based on current langcode.
      'filter[lang][condition][path]': 'langcode',
      'filter[lang][condition][operator]': '=',
      'filter[lang][condition][value]': drupalSettings.path.currentLanguage,

      // Sort in ascending order.
      sort: 'field_popular_dates_start',
    });

    const API_URL =
      `${drupalSettings.base_url}/${drupalSettings.path.currentLanguage}` +
      `/jsonapi/taxonomy_term/popular_booking_dates?${params.toString()}`;

    fetch(API_URL, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/vnd.api+json',
      },
    }).then((response) => {
      if (response.ok) {
        response.json().then((res) => {
          const popularDatesData = res.data;
          setPopularDates({ loading: false, data: popularDatesData });

          const popularDateValue = urlParams.get('popularDate') ?? 'default';
          if (popularDateValue !== 'default') {
            setPopularDatesSelected(popularDateValue);

            if (
              props.setSelectedPopularDate &&
              popularDatesData[parseInt(popularDateValue)]
            ) {
              props.setSelectedPopularDate({
                startDate,
                endDate,
                name: popularDatesData[parseInt(popularDateValue)].attributes
                  .name,
              });
            }
          }
        });
      } else {
        console.log('error getting data');
      }
    });
  }, [setPopularDates]);

  function getCampaignDates() {
    const campaignDatesArray = [];
    if (props.activeCampaigns) {
      props.activeCampaigns.map(({ attributes }) => {
        if (
          !attributes.field_exclude_from_calendar &&
          attributes.field_campaign_validity
        ) {
          attributes.field_campaign_validity.forEach((date) => {
            let range = moment.range(date.value, date.end_value);
            campaignDatesArray.push(Array.from(range.by('day')));
            setCampaignDates(campaignDatesArray.flat());
          });
        }
      });
    }
  }

  useEffect(() => {
    getCampaignDates();
  }, [props.activeCampaigns]);

  useEffect(() => {
    // The book button should only be enabled once we have checked the
    // availability for the selected dates.
    if (!availabilityChecked && props.availableDates && startDate && endDate) {
      const startDateFormatted = startDate.format('YYYY-MM-DD');
      const endDateFormatted = endDate.format('YYYY-MM-DD');
      if (
        props.availableDates.some((day) => startDateFormatted === day.date) &&
        props.availableDates.some((day) => endDateFormatted === day.date)
      ) {
        setAvailabilityChecked(true);
      }
    }

    // Check if the owner should be allowed to do a quick reservation.
    if (
      moment(startDate).diff(moment(), 'days') >
      parseInt(drupalSettings.quick_reservation_deadline)
    ) {
      setQuickReservationAllowed(false);
    } else {
      setQuickReservationAllowed(true);
    }
  }, [props.availableDates, onDatesChange]);

  useEffect(() => {
    if (props.userIsOwner) {
      setMinimumStayFordate([]);
    } else if (startDate) {
      checkMinimumDate(startDate, endDate);
    }
  }, [props.userIsOwner]);

  props.availableDates
    ? props.availableDates.map((day) => {
        if (day.minimumStay > 1) {
          const { date, minimumStay } = day;
          minimumstayDates.push({ date: moment(date), minimumStay });
        }
        if (day.availabilityLevel === 'zero') {
          unavailableDates.push(day.date);
        }
        if (day.availabilityLevel === 'limited') {
          limitedDates.push(day.date);
        }
        if (day.availabilityLevel === 'minimum') {
          minimalDates.push(day.date);
        }
      })
    : null;

  const isBlocked = (calendarDate) => {
    const day = moment(calendarDate);

    if (isOutsideRange(day)) {
      return true;
    }

    if (props.userOwnsApartment) {
      return false;
    }

    let yesterday = moment(day).add(-1, 'days');

    if (props.availableDates) {
      // Block unavailable dates, but make it possible to select blocked date as departure date.
      if (!startDate) {
        if (
          unavailableDates.some((date) => day.isSame(date, 'day')) ||
          minimalDates.some((date) => day.isSame(date, 'day'))
        ) {
          return true;
        }
      }
      if (
        (unavailableDates.some((date) => day.isSame(date, 'day')) &&
          unavailableDates.some((date) => yesterday.isSame(date, 'day'))) ||
        (minimalDates.some((date) => day.isSame(date, 'day')) &&
          minimalDates.some((date) => yesterday.isSame(date, 'day')))
      ) {
        return true;
      }
    } else {
      return false;
    }

    // Disable day if minimum stay is in effect for the chosen start date.
    if (
      minimumStayForDate.some((date) => day.isSame(date, 'day')) &&
      !day.isSame(minimumStayForDate[minimumStayForDate.length - 1], 'day')
    ) {
      return true;
    }
  };

  function checkMinimumDate(startDate, endDate) {
    const minStayDate = minimumstayDates.find((day) =>
      startDate.isSame(day.date, 'day')
    );

    if (!minStayDate) {
      setMinimumStayFordate([]);
      return;
    }

    const { minimumStay } = minStayDate;

    if (endDate) {
      if (startDate.diff(endDate, 'days') >= minimumStay) {
        setMinimumStayFordate([]);
        return;
      }
    }

    const minimumDates = [];
    const { date } = minStayDate;
    for (let i = 0; i < minimumStay; i++) {
      date.add(1, 'days');
      minimumDates.push(date.clone());
    }
    setMinimumStayFordate(minimumDates);
  }

  // Dates used for checking if a day is outside of daterange.
  function checkForRestrictedDates(start, end) {
    const diff = moment(end).diff(start, 'days') + 1;

    for (let i = 0; i < diff; i++) {
      const checkDate = moment(start).add(i, 'd').format(API_DATE_FORMAT);

      if (
        unavailableDates.includes(checkDate) ||
        minimalDates.includes(checkDate)
      ) {
        return setRestrictedDate(checkDate);
      }
    }
    return setRestrictedDate(null);
  }

  function checkForBlockedDates(start, end) {
    if (!start || !end) {
      return setBlockedDate(null);
    }

    const diff = moment(end).diff(start, 'days');

    // The end date won't checked, because the user can
    // select an otherwise blocked date as a departure date.
    for (let i = 0; i < diff; i++) {
      const checkDate = moment(start).add(i, 'd').format(API_DATE_FORMAT);
      if (
        unavailableDates.includes(checkDate) ||
        minimalDates.includes(checkDate)
      ) {
        return setBlockedDate(checkDate);
      }
    }
    return setBlockedDate(null);
  }

  useEffect(() => {
    onDatesChange(props.startDate, props.endDate);
  }, [props.startDate, props.endDate]);

  function onDatesChange(startDate, endDate) {
    // No checks required if the user owns the apartment.
    if (props.userOwnsApartment) {
      props.setStartDate(startDate);
      props.setEndDate(endDate);

      return;
    }

    if (props.availableDates) {
      setAvailabilityChecked(false);
    }
    if (startDate) {
      const lastDate =
        endDate ||
        moment(startDate).add(
          62, // How many days into future will be looked for blocked dates.
          'days'
        );
      checkForRestrictedDates(startDate, lastDate);
      checkForBlockedDates(startDate, lastDate);

      if (minimumstayDates.length > 0 && !props.userIsOwner) {
        checkMinimumDate(startDate, endDate);
      }
    }

    props.setStartDate(startDate);
    props.setEndDate(endDate);
  }

  function ownerButtonIsDisabled() {
    if (props.disableOwnerReservation() || !startDate || !endDate) {
      return 'button--disabled';
    } else {
      return false;
    }
  }

  function isOutsideRange(day) {
    if (startDate && restrictedDate) {
      return (
        day.isBefore(moment(startDate)) ||
        day.isAfter(moment(restrictedDate), 'day')
      );
    } else {
      // Set day outside of range if booking deadline is passed.
      if (day.isSame(moment(), 'day') && isBookingDeadline) {
        return true;
      }
      return !isInclusivelyAfterDay(day, moment());
    }
  }

  useEffect(() => {
    checkForBlockedDates(startDate, endDate);
  }, [props.availableDates]);

  useEffect(() => {
    if (props.userOwnsApartment) {
      return setDatesAvailable(true);
    }

    if (
      (blockedDate && moment(blockedDate).isSameOrBefore(endDate)) ||
      !isInclusivelyAfterDay(startDate, moment())
    ) {
      if (startDate && endDate) {
        if (
          minimalDates.includes(blockedDate) ||
          unavailableDates.includes(blockedDate)
        ) {
          setDatesAvailable(false);
        }
      }
      props.setStartDate(undefined);
      props.setEndDate(undefined);
    } else {
      setDatesAvailable(true);
    }
  }, [checkForBlockedDates]);

  // Reset popular dates select input if dates don't match current selection.
  useEffect(() => {
    if (endDate) {
      if (
        popularDatesEndValue !== moment(endDate).format(API_DATE_FORMAT) ||
        (popularDatesStartValue !== moment(startDate).format(API_DATE_FORMAT) &&
          popularDatesSelected !== 'default')
      ) {
        if (props.setSelectedPopularDate) {
          props.setSelectedPopularDate('default');
        }
        setPopularDatesSelected('default');
      }
    } else {
      if (props.setSelectedPopularDate) {
        props.setSelectedPopularDate('default');
      }
      setPopularDatesSelected('default');
    }
  }, [endDate]);

  // Color calendar days by avalability and campaigns.
  const renderDayContents = (calendarDate) => {
    const classes = [];

    const day = moment(calendarDate);

    if (
      !props.userOwnsApartment &&
      campaignDates &&
      campaignDates.some((date) => day.isSame(date, 'day'))
    ) {
      classes.push(
        'react-calendar__month-view__days__day--campaign-availability'
      );
    }

    if (
      props.availableDates &&
      !(day.isSame(moment(), 'day') && isBookingDeadline)
    ) {
      if (unavailableDates.some((date) => day.isSame(date, 'day'))) {
        classes.push('react-calendar__month-view__days__day--blocked');
      } else if (limitedDates.some((date) => day.isSame(date, 'day'))) {
        classes.push('react-calendar__month-view__days__day--limited');
      } else if (minimalDates.some((date) => day.isSame(date, 'day'))) {
        classes.push('react-calendar__month-view__days__day--minimal');
      }
    }

    if (minimumStayForDate && minimumStayForDate.length > 0) {
      if (minimumStayForDate.some((date) => day.isSame(date, 'day'))) {
        classes.push('react-calendar__month-view__days__day--minimum-booking');
      }
    }

    return classes.join(' ');
  };

  // Show information bar on the bottom of calendar.
  const renderCalendarInfo = () => {
    let calendarInfo = '';
    if (props.availableDates) {
      calendarInfo = (
        <>
          <div className="datepicker-footer__date datepicker-footer__date--available">
            <span className="datepicker-footer__icon datepicker-footer__icon--available-icon"></span>
            {Drupal.t('Available date')}
          </div>
          <div className="datepicker-footer__date datepicker-footer__date--limited">
            <span className="datepicker-footer__icon datepicker-footer__icon--limited-icon"></span>
            {Drupal.t('Limited availability')}
          </div>
          <div className="datepicker-footer__date datepicker-footer__date--campaign">
            <span className="datepicker-footer__icon datepicker-footer__icon--campaign-icon"></span>
            {Drupal.t('Campaign benefit available')}
          </div>
          <div className="datepicker-footer__date datepicker-footer__date--minimal">
            <span className="datepicker-footer__icon datepicker-footer__icon--minimal-icon"></span>
            <div>
              {Drupal.t('Almost full')}
              <div
                className="datepicker-footer__date--minimal__description"
                dangerouslySetInnerHTML={{
                  __html: minimumAvailabilityMessage,
                }}
              ></div>
            </div>
          </div>
          <div className="datepicker-footer__date datepicker-footer__date--minimum-stay">
            <span className="datepicker-footer__icon datepicker-footer__icon--minimum-stay-icon"></span>
            <div>
              {Drupal.t('Minimum duration of visit')}
              <div className="datepicker-footer__date--minimal__description">
                {drupalSettings.min_stay_description}
              </div>
            </div>
          </div>
          <div className="datepicker-footer__date datepicker-footer__date--unavailable">
            <span className="datepicker-footer__icon datepicker-footer__icon--unavailable-icon"></span>
            {Drupal.t('Booked')}
          </div>
        </>
      );
    } else {
      calendarInfo = (
        <p className="datepicker-footer--minimum-stay--info">
          {Drupal.t('* Some of the destinations include a minimum stay.')}
        </p>
      );
    }

    return (
      <>
        <hr />
        <div className="datepicker-footer">{calendarInfo}</div>
      </>
    );
  };

  return (
    <>
      <div
        className={`accommodation-reservation-form ${hideButtonClass} ${
          props.userOwnsApartment ? 'owned-apartment' : ''
        }`}
      >
        <form>
          <div className="accommodation-reservation-form__options-wrapper">
            <div className="accommodation-reservation-form__datepicker">
              <label htmlFor="ruka_start_date" className="form-item__label">
                {props.selectDatesLabel}
              </label>
              {!props.loadingAvailabilities && props.availabilityFound ? (
                <DatePicker
                  startDate={props.startDate}
                  endDate={props.endDate}
                  startDateId={'ruka_start_date'}
                  endDateId={'ruka_end_date'}
                  setStartDate={props.setStartDate}
                  setEndDate={props.setEndDate}
                  dayIsDisabled={isBlocked}
                  parseDay={renderDayContents}
                  content={renderCalendarInfo()}
                  useMoment={true}
                />
              ) : !props.availabilityFound ? (
                <ErrorMessage message={Drupal.t('No availability found')} />
              ) : (
                `${Drupal.t('Loading available dates')}...`
              )}
            </div>

            {!props.errorMessage && (
              <>
                {!datesAvailable && (
                  <ErrorMessage
                    message={Drupal.t('No availability for the period')}
                  />
                )}
              </>
            )}

            <div className="accommodation-reservation-form__select-popular-dates">
              <label htmlFor="popularDates" className="form-item__label">
                {props.popularDatesLabel}
              </label>
              <div className="form-item__dropdown">
                <select
                  id="popularDates"
                  className="form-item__select"
                  onChange={(e) => {
                    selectPopularDates(e);
                  }}
                  value={popularDatesSelected}
                >
                  {popularDates.loading ? (
                    <option>{Drupal.t('Loading')}</option>
                  ) : (
                    <option value="default">{Drupal.t('Select')}</option>
                  )}
                  {popularDates.data
                    ? popularDates.data.map(({ attributes }, index) => (
                        <option
                          key={index}
                          data-start={attributes.field_popular_dates_start}
                          data-end={attributes.field_popular_dates_end}
                          value={index}
                        >
                          {`${attributes.name} (${moment(
                            attributes.field_popular_dates_start
                          ).format(READABLE_DATE_FORMAT)} - ${moment(
                            attributes.field_popular_dates_end
                          ).format(READABLE_DATE_FORMAT)})`}
                        </option>
                      ))
                    : null}
                </select>
              </div>
            </div>

            <NumberInput
              id="adults"
              class="number-input--adults"
              min={1}
              max={
                props.maxGuests
                  ? props.maxGuests - props.kids
                  : drupalSettings.active_site == 'Ruka'
                  ? 8 - props.kids
                  : 10 - props.kids
              }
              label={props.adultsLabel}
              value={props.adults}
              setter={props.setAdults}
              areaLabelIncrease="Increase number of Adults"
              areaLabelDecrease="Decrease number of Adults"
              disabled={
                props.setDatesFirst && (!props.startDate || !props.endDate)
              }
            />

            <NumberInput
              id="children-4-12"
              class="number-input--children"
              label={props.kidsLabel}
              value={props.kids}
              setter={props.setChildren}
              max={
                props.maxGuests
                  ? props.maxGuests - props.adults
                  : drupalSettings.active_site == 'Ruka'
                  ? 8 - props.adults
                  : 9 - props.adults
              }
              disabled={
                props.setDatesFirst && (!props.startDate || !props.endDate)
              }
            />

            {props.allowInfants ? (
              <NumberInput
                id="children-0-3"
                class="number-input--children"
                label={props.infantsLabel}
                value={props.infants}
                setter={props.setInfants}
                disabled={
                  props.setDatesFirst && (!props.startDate || !props.endDate)
                }
              />
            ) : null}
          </div>

          <div className="accommodation-reservation-form__filters-wrapper">
            {props.errorMessage ? (
              <ErrorMessage message={props.errorMessage} />
            ) : null}
          </div>
          <div className="accommodation-reservation-form__extra-services">
            {((props.extraServices && props.extraServices.length > 0) ||
              props.roomAllowsPets) && (
              <div>
                {props.setDatesFirst && <h4>{Drupal.t('Extra services')}</h4>}

                {props.roomAllowsPets && (
                  <div className="accommodation-reservation-form__pets">
                    <label className="form-item__label form-item__label--checkbox">
                      <input
                        name="pets"
                        id="pets"
                        type="checkbox"
                        className="visually-hidden"
                        checked={props.pets}
                        onChange={(e) => props.setPets(e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      {Drupal.t('Travelling with a pet (+0€)')}
                    </label>
                  </div>
                )}

                {startDate &&
                  endDate &&
                  props.extraServices &&
                  props.extraServices
                    .filter((service) =>
                      overlapsDateRange(service.available_dates)
                    )
                    .map((service) => (
                      <label
                        className="form-item__label form-item__label--checkbox"
                        key={service.id}
                      >
                        <input
                          name={`service-${service.id}`}
                          id={`service-${service.id}`}
                          checked={props.selectedServices.includes(service.id)}
                          type="checkbox"
                          className="visually-hidden"
                          onChange={props.onExtraServiceChange}
                          value={service.id}
                        />
                        <span className="checkmark"></span>
                        {service.name} (+{service.price}€)
                      </label>
                    ))}
              </div>
            )}

            {startDate &&
            endDate &&
            availabilityChecked &&
            !props.disableBooking() ? (
              // Booking widget button.
              <button
                onClick={(e) => props.onSubmit(e)}
                className="button button--large-full-width"
              >
                {Drupal.t(props.buttonText)}{' '}
                {new Intl.NumberFormat('fi-FI', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(parseFloat(props.price))}
              </button>
            ) : props.availableDates && !props.userOwnsApartment ? (
              // Disabled booking widget button.
              <>
                <button
                  className="button--disabled button--large-full-width"
                  disabled={true}
                  data-tip={Drupal.t('You must select available dates first')}
                >
                  {(!availabilityChecked && startDate && endDate) ||
                  props.isSubmitting ||
                  (props.loadingAccommodations &&
                    props.availabilityFound &&
                    !props.errorMessage)
                    ? Drupal.t('Loading')
                    : Drupal.t(props.buttonText)}
                </button>

                {(props.loadingAccommodations && !props.errorMessage) ||
                props.isSubmitting ? null : (
                  <ReactTooltip place="bottom" type="dark" effect="solid" />
                )}
              </>
            ) : !props.userOwnsApartment ? (
              // Default button.
              <button
                onClick={async (e) => props.onSubmit(e)}
                className="button button--large-full-width"
              >
                {props.buttonText}
              </button>
            ) : (
              <div>
                {/* Owners buttons */}
                {props.hasQuota && (
                  <>
                    <button
                      onClick={async (e) => props.onSubmit(e, 'owner')}
                      className={`button button--large-full-width ${ownerButtonIsDisabled()}`}
                      disabled={ownerButtonIsDisabled()}
                    >
                      {Drupal.t('For own use')}
                    </button>
                    {drupalSettings.purchase_info_own_use && (
                      <Accordion
                        className={'accordion-react--toggle-button'}
                        content={
                          <div className="accommodation-reservation-form__purchase-info-own-use">
                            {drupalSettings.purchase_info_own_use}
                          </div>
                        }
                        title={<span>{Drupal.t('Detailed conditions')}</span>}
                      />
                    )}
                  </>
                )}
                {/* Quick buttons */}
                <button
                  onClick={async (e) => props.onSubmit(e, 'quick')}
                  className={`button button--large-full-width ${
                    ownerButtonIsDisabled() || !quickReservationAllowed
                      ? 'button--disabled'
                      : ''
                  }`}
                  disabled={ownerButtonIsDisabled() || !quickReservationAllowed}
                >
                  {Drupal.t('Quick reservation')}
                </button>
                {!quickReservationAllowed && (
                  <div className="accommodation-reservation-form__purchase-info-quick-resrv">
                    {Drupal.t(
                      'Quick reservation should begin within @deadline days of booking',
                      {
                        '@deadline': drupalSettings.quick_reservation_deadline,
                      }
                    )}
                  </div>
                )}
                {drupalSettings.purchase_info_quick_reserv && (
                  <Accordion
                    className={'accordion-react--toggle-button'}
                    content={
                      <div className="accommodation-reservation-form__purchase-info-quick-resrv">
                        {drupalSettings.purchase_info_quick_reserv}
                      </div>
                    }
                    title={<span>{Drupal.t('Detailed conditions')}</span>}
                  />
                )}
              </div>
            )}
          </div>
          {props.userIsOwner && !props.userOwnsApartment && (
            <div
              dangerouslySetInnerHTML={{
                __html: drupalSettings.owner_price_text,
              }}
            ></div>
          )}
        </form>
      </div>
    </>
  );
};

AccommodationSearchForm.propTypes = {
  startDate: PropTypes.object,
  setStartDate: PropTypes.func,
  endDate: PropTypes.object,
  setEndDate: PropTypes.func,
  adults: PropTypes.number,
  setAdults: PropTypes.func,
  kids: PropTypes.number,
  setChildren: PropTypes.func,
  infants: PropTypes.number,
  setInfants: PropTypes.func,
  pets: PropTypes.bool,
  setPets: PropTypes.func,
  hideButton: PropTypes.bool,
  onSubmit: PropTypes.func,
  availableDates: PropTypes.array,
  buttonText: PropTypes.string,
  price: PropTypes.number,
  fetchingMessage: PropTypes.string,
  loadingAvailabilities: PropTypes.bool,
  maxGuests: PropTypes.number,
  errorMessage: PropTypes.string,
  availabilityFound: PropTypes.bool,
  roomAllowsPets: PropTypes.bool,
  allowInfants: PropTypes.bool,
  loadingAccommodations: PropTypes.bool,
  activeCampaigns: PropTypes.array,
  setDatesFirst: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  userOwnsApartment: PropTypes.bool,
  userIsOwner: PropTypes.bool,
  selectDatesLabel: PropTypes.string,
  popularDatesLabel: PropTypes.string,
  adultsLabel: PropTypes.string,
  kidsLabel: PropTypes.string,
  infantsLabel: PropTypes.string,
  startDatePlaceholderText: PropTypes.string,
  endDatePlaceholderText: PropTypes.string,
  setPopularDateValue: PropTypes.func,
  setSelectedPopularDate: PropTypes.func,
  hasQuota: PropTypes.bool,
  disableOwnerReservation: PropTypes.func,
  disableBooking: PropTypes.func,
  extraServices: PropTypes.array,
  onExtraServiceChange: PropTypes.func,
  selectedServices: PropTypes.array,
};

export default AccommodationSearchForm;
